import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  collapseAllExcept,
  computeInitialCollapsedPaths,
  findSearchMatches,
  flattenTree,
  getAncestorRowKeys,
  getRowKey,
} from '../model/flatten-tree';
import {
  addArrayItemWithTemplate,
  deleteAtPath,
  getUniqueObjectKey,
  getValueAtPath,
  setValueAtPath,
} from '../model/json-path';
import { validateSafeUpdate } from '../model/validate';
import { defaultTheme } from '../styles/theme';
import '../styles/safe-json-editor.css';
import type { JsonKind, JsonPath, JsonValue, SafeJsonEditorProps } from '../types';
import { FieldPanel } from './FieldPanel';
import { VirtualizedTree } from './VirtualizedTree';

const DEFAULT_LARGE_DOCUMENT_THRESHOLD = 500;
const DEFAULT_AUTO_COLLAPSE_DEPTH = 2;

const TYPE_DEFAULTS: Record<JsonKind, JsonValue> = {
  object: {},
  array: [],
  string: '',
  number: 0,
  boolean: false,
  null: null,
};

/**
 * Restriction-aware JSON tree editor. Combines a virtualized, auto-collapsing
 * tree navigator (left) with a focused "conf"-style edit panel for the
 * selected node (right) — a Sditor-inspired split layout adapted for editing
 * JSON data values instead of JSON Schema. When `canEditStructure` is false,
 * the baked-in safe-edit policy applies: every disallowed action simply has
 * no button (proactive gating), and `validateSafeUpdate` runs as a final
 * defense-in-depth check before every change is emitted.
 */
export const SafeJsonEditor: React.FC<SafeJsonEditorProps> = ({
  value,
  onChange,
  canEditStructure = false,
  height = 480,
  largeDocumentThreshold = DEFAULT_LARGE_DOCUMENT_THRESHOLD,
  autoCollapseDepth = DEFAULT_AUTO_COLLAPSE_DEPTH,
  theme: themeOverride,
  onValidationError,
}) => {
  const theme = useMemo(() => ({ ...defaultTheme, ...themeOverride }), [themeOverride]);

  // Baseline for the safe-edit policy: the value as it was when this editor
  // session started, mirroring how a host app's "initial value" stays fixed
  // for the duration of an edit session/modal.
  const originalValueRef = useRef(value);

  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() =>
    computeInitialCollapsedPaths(value, { autoCollapseDepth, totalNodeCountThreshold: largeDocumentThreshold }),
  );
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [scrollToRowKey, setScrollToRowKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const rows = useMemo(() => flattenTree(value, { collapsedPaths, searchQuery }), [value, collapsedPaths, searchQuery]);
  const searchMatches = useMemo(() => findSearchMatches(value, searchQuery), [value, searchQuery]);

  // Whenever the current match changes (new query, or Prev/Next), declutter
  // the tree down to just that match's ancestor chain — rather than only
  // ever expanding more, which would leave every previously-visited branch
  // (from earlier matches or manual browsing) expanded and cluttered.
  useEffect(() => {
    if (!searchMatches.length) {
      setScrollToRowKey(null);
      return;
    }

    const match = searchMatches[Math.min(activeMatchIndex, searchMatches.length - 1)];
    const ancestorKeys = new Set(getAncestorRowKeys(match.path));

    setCollapsedPaths(collapseAllExcept(value, ancestorKeys));
    setScrollToRowKey(match.rowKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatches, activeMatchIndex]);

  const selectedPath = useMemo<JsonPath | null>(() => {
    if (!selectedRowKey) return null;
    const row = rows.find((candidate) => candidate.rowKey === selectedRowKey);
    if (row) return row.path;
    // Selected row is temporarily out of the flattened list (e.g. an
    // ancestor got collapsed) — fall back to decoding the key so the panel
    // doesn't blank out unnecessarily.
    if (selectedRowKey === 'root') return [];
    try {
      return JSON.parse(selectedRowKey) as JsonPath;
    } catch {
      return null;
    }
  }, [rows, selectedRowKey]);

  const applyChange = (nextValue: JsonValue) => {
    const validationError = canEditStructure ? null : validateSafeUpdate(originalValueRef.current, nextValue);
    if (validationError) {
      setErrorMessage(validationError);
      onValidationError?.(validationError);
      return;
    }
    setErrorMessage(null);
    onChange(nextValue);
  };

  const handleToggleCollapse = (rowKey: string) => {
    setCollapsedPaths((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleUpdatePrimitive = (path: JsonPath, nextValue: JsonValue) => {
    applyChange(setValueAtPath(value, path, nextValue));
  };

  const handleRenameKey = (path: JsonPath, nextKeyRaw: string) => {
    const nextKey = nextKeyRaw.trim();
    const currentKey = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    const parent = getValueAtPath(value, parentPath);

    if (!nextKey || typeof currentKey !== 'string' || parent === null || Array.isArray(parent) || typeof parent !== 'object') {
      return;
    }
    if (nextKey === currentKey) return;

    const parentObject = parent as Record<string, JsonValue>;
    const stableKey = getUniqueObjectKey(parentObject, nextKey);
    const nextParent: Record<string, JsonValue> = {};
    Object.entries(parentObject).forEach(([key, childValue]) => {
      nextParent[key === currentKey ? stableKey : key] = childValue;
    });

    applyChange(setValueAtPath(value, parentPath, nextParent));
  };

  const handleConvertType = (path: JsonPath, nextType: JsonKind) => {
    applyChange(setValueAtPath(value, path, TYPE_DEFAULTS[nextType]));
  };

  const handleAddObjectKey = (path: JsonPath, key: string, keyValue: JsonValue) => {
    const node = getValueAtPath(value, path);
    if (node === null || Array.isArray(node) || typeof node !== 'object') return;

    const current = node as Record<string, JsonValue>;

    // Defense in depth: the Add Key dialog already blocks duplicate names,
    // but this must never silently rename or overwrite an existing key if a
    // duplicate somehow gets through.
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      const message = `Key "${key}" already exists here.`;
      setErrorMessage(message);
      onValidationError?.(message);
      return;
    }

    applyChange(setValueAtPath(value, path, { ...current, [key]: keyValue }));
  };

  const handleRemoveKey = (path: JsonPath) => {
    applyChange(deleteAtPath(value, path));
    setSelectedRowKey(null);
  };

  const handleAddArrayItem = (path: JsonPath, index: number, position: 'above' | 'below') => {
    const array = getValueAtPath(value, path);
    if (!Array.isArray(array)) return;
    applyChange(setValueAtPath(value, path, addArrayItemWithTemplate(array, index, position)));
  };

  const handleRemoveArrayItem = (path: JsonPath) => {
    applyChange(deleteAtPath(value, path));
    setSelectedRowKey(null);
  };

  const handleNavigateToPath = (path: JsonPath) => {
    // Reveal the child by expanding whatever is currently selected (its
    // parent container) if it happens to be collapsed, then select and
    // scroll to the child itself — mirrors clicking it directly in the tree.
    if (selectedPath) {
      const parentRowKey = getRowKey(selectedPath);
      setCollapsedPaths((current) => {
        if (!current.has(parentRowKey)) return current;
        const next = new Set(current);
        next.delete(parentRowKey);
        return next;
      });
    }

    const childRowKey = getRowKey(path);
    setSelectedRowKey(childRowKey);
    setScrollToRowKey(childRowKey);
  };

  const goToMatch = (direction: 1 | -1) => {
    if (!searchMatches.length) return;
    setActiveMatchIndex((current) => (current + direction + searchMatches.length) % searchMatches.length);
  };

  return (
    <div className="sje-root" style={{ height }}>
      <div className="sje-tree-pane">
        <div className="sje-toolbar">JSON</div>
        <div className="sje-search">
          <input
            aria-label="Search keys, path, or values"
            placeholder="Search keys, path, or values"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setActiveMatchIndex(0);
            }}
          />
          <button type="button" onClick={() => goToMatch(-1)} disabled={!searchMatches.length}>
            Prev
          </button>
          <button type="button" onClick={() => goToMatch(1)} disabled={!searchMatches.length}>
            Next
          </button>
          {searchQuery.trim() ? (
            <span className="sje-search-count">
              {searchMatches.length
                ? `${Math.min(activeMatchIndex, searchMatches.length - 1) + 1}/${searchMatches.length}`
                : '0/0'}
            </span>
          ) : null}
        </div>
        {errorMessage ? <div className="sje-error-banner">{errorMessage}</div> : null}
        <VirtualizedTree
          rows={rows}
          collapsedPaths={collapsedPaths}
          onToggleCollapse={handleToggleCollapse}
          selectedRowKey={selectedRowKey}
          onSelectRow={setSelectedRowKey}
          theme={theme}
          scrollToRowKey={scrollToRowKey}
        />
      </div>
      <div className="sje-panel-pane">
        <div className="sje-toolbar">Edit</div>
        <FieldPanel
          value={value}
          selectedPath={selectedPath}
          canEditStructure={canEditStructure}
          theme={theme}
          onUpdatePrimitive={handleUpdatePrimitive}
          onRenameKey={handleRenameKey}
          onConvertType={handleConvertType}
          onAddObjectKey={handleAddObjectKey}
          onRemoveKey={handleRemoveKey}
          onAddArrayItem={handleAddArrayItem}
          onRemoveArrayItem={handleRemoveArrayItem}
          onNavigateToPath={handleNavigateToPath}
        />
      </div>
    </div>
  );
};
